import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMachinesQuery } from "@/hooks/queries/useMachinesQuery";
import styles from "./FallbackNotification.module.css";

const CONNECTIVITY_PATTERNS = [
  /HTTP\s*50[234]/i,
  /network\s*error/i,
  /failed\s*to\s*fetch/i,
  /AbortError/i,
  /timeout/i,
  /indispon/i,
];

function isConnectivityError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "";
  if (!msg) return false;
  return CONNECTIVITY_PATTERNS.some((re) => re.test(msg));
}

/**
 * Mirrors Vue's `FallbackNotification.vue`. Surfaces a non-blocking warning
 * when the external machines API is unavailable so the user understands
 * why the grid is empty / stale.
 *
 * Trigger: the `useMachinesQuery` hook errors out with a connectivity-shaped
 * message. The user can dismiss; we re-arm when a fresh error of a different
 * shape arrives (mirroring Vue's "previousError" check).
 */
export default function FallbackNotification() {
  const { t } = useTranslation();
  const machinesQ = useMachinesQuery();
  const [dismissed, setDismissed] = useState(false);
  const lastErrorRef = useRef<string | null>(null);

  const errorMsg =
    machinesQ.error instanceof Error
      ? machinesQ.error.message
      : machinesQ.error
        ? String(machinesQ.error)
        : null;

  useEffect(() => {
    if (errorMsg && errorMsg !== lastErrorRef.current) {
      lastErrorRef.current = errorMsg;
      setDismissed(false);
    } else if (!errorMsg) {
      lastErrorRef.current = null;
    }
  }, [errorMsg]);

  if (dismissed) return null;
  if (!isConnectivityError(machinesQ.error)) return null;

  return (
    <div
      className={styles.notification}
      role="alert"
      aria-live="polite"
    >
      <div className={styles.content}>
        <div className={styles.icon} aria-hidden="true">
          ⚠️
        </div>
        <div className={styles.text}>
          <strong>{t("fallback.apiUnavailableTitle")}</strong>
          <p>{t("fallback.apiUnavailableText")}</p>
        </div>
        <button
          type="button"
          className={styles.dismiss}
          title="Dispensar"
          aria-label="Dispensar"
          onClick={() => setDismissed(true)}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
